import React, { useEffect, useState } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import CssBaseline from "@material-ui/core/CssBaseline";
import Typography from "@material-ui/core/Typography";

import { authPalette } from "../../theme/authPalette";
import { useWebInfo } from "../../context/WebInfoContext";
import { getVersionText } from "../../config/version";
import { i18n } from "../../translate/i18n";
import { getWebInfo } from "../../services/settings.api";

/**
 * Marco de las pantallas de acceso (doc 0446, fase 3): acceso, registro y recuperación.
 * Formulario limpio a la izquierda y, en escritorio, un panel de marca con una vista
 * previa de la bandeja simple. Colores de `authPalette` (doc 0390): responde al modo.
 */
const useStyles = makeStyles((theme) => {
  const ap = authPalette(theme.mode);
  const dark = theme.mode === "dark";
  return {
    root: {
      minHeight: "100vh",
      display: "flex",
      background: ap.cardBackgroundSolid,
    },
    formSide: {
      flex: "1 1 50%",
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      padding: "28px 32px",
      [theme.breakpoints.down("xs")]: { padding: "20px 16px" },
    },
    brandRow: { display: "flex", alignItems: "center", gap: 10, minHeight: 44 },
    logo: { maxHeight: 40, maxWidth: 160, objectFit: "contain" },
    brandName: { fontSize: 17, fontWeight: 700, color: ap.textStrong },
    formWrap: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      width: "100%",
      maxWidth: 400,
      margin: "0 auto",
      padding: "32px 0",
    },
    title: { fontSize: 30, fontWeight: 700, letterSpacing: -0.5, color: ap.textStrong, lineHeight: 1.15 },
    subtitle: { fontSize: 15, color: ap.textMuted, marginTop: 8, marginBottom: 28, lineHeight: 1.5 },
    footer: { textAlign: "center", fontSize: 12, color: ap.textMuted },
    version: {
      display: "inline-block",
      marginLeft: 8,
      padding: "2px 8px",
      borderRadius: 999,
      background: ap.chipBackground,
      color: ap.chipText,
      fontSize: 11,
      fontWeight: 700,
    },
    brandSide: {
      flex: "1 1 50%",
      position: "relative",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      padding: "48px 56px",
      color: "#FFFFFF",
      background: dark
        ? "linear-gradient(150deg, #0b1a3a 0%, #1e3a8a 100%)"
        : "linear-gradient(150deg, #1e3a8a 0%, #2563eb 100%)",
      [theme.breakpoints.down("sm")]: { display: "none" },
    },
    brandImage: {
      flex: "1 1 50%",
      backgroundColor: "#0b1a3a",
      backgroundSize: "cover",
      backgroundPosition: "center",
      [theme.breakpoints.down("sm")]: { display: "none" },
    },
    glow: {
      position: "absolute",
      width: 420,
      height: 420,
      borderRadius: "50%",
      right: -120,
      top: -120,
      background: "radial-gradient(circle, rgba(255,255,255,0.18), transparent 70%)",
      pointerEvents: "none",
    },
    heroTitle: { fontSize: 34, fontWeight: 700, lineHeight: 1.15, letterSpacing: -0.5, maxWidth: 460 },
    heroSubtitle: { fontSize: 16, opacity: 0.85, marginTop: 14, maxWidth: 440, lineHeight: 1.55 },
    preview: {
      marginTop: 40,
      maxWidth: 420,
      borderRadius: 18,
      background: "#FFFFFF",
      color: "#111827",
      boxShadow: "0 24px 60px rgba(2,6,23,0.35)",
      overflow: "hidden",
    },
    previewHead: {
      display: "flex",
      gap: 6,
      padding: "14px 16px 10px",
      borderBottom: "1px solid #EEF1F6",
    },
    chip: { fontSize: 12, fontWeight: 600, padding: "4px 10px", borderRadius: 999, background: "#F1F3F6", color: "#374151" },
    chipOn: { background: "#DDE2EE", color: "#1E3A8A" },
    row: { display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", borderBottom: "1px solid #F3F4F6" },
    avatar: {
      width: 40,
      height: 40,
      borderRadius: "50%",
      color: "#FFFFFF",
      fontWeight: 600,
      fontSize: 14,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    },
    rowText: { flex: 1, minWidth: 0 },
    rowName: { fontSize: 14, fontWeight: 600 },
    rowMsg: { fontSize: 13, color: "#4B5563", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
    rowMeta: { display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 },
    rowTime: { fontSize: 11, color: "#15803D", fontWeight: 600 },
    badge: {
      minWidth: 18,
      height: 18,
      borderRadius: 9,
      background: "#15803D",
      color: "#FFFFFF",
      fontSize: 11,
      fontWeight: 700,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "0 5px",
    },
    channels: { display: "flex", gap: 8, marginTop: 28, flexWrap: "wrap" },
    channel: {
      fontSize: 12,
      fontWeight: 600,
      padding: "6px 12px",
      borderRadius: 999,
      background: "rgba(255,255,255,0.14)",
      border: "1px solid rgba(255,255,255,0.22)",
    },
  };
});

/** Estilos de formulario compartidos por las pantallas de acceso. */
export const useAuthFormStyles = makeStyles((theme) => {
  const ap = authPalette(theme.mode);
  return {
    form: { display: "flex", flexDirection: "column", gap: 16 },
    field: {
      "& .MuiOutlinedInput-root": { borderRadius: 12, background: ap.inputBackground },
      "& .MuiOutlinedInput-notchedOutline": { borderColor: ap.inputBorder },
    },
    submit: {
      height: 48,
      borderRadius: 999,
      textTransform: "none",
      fontSize: 15,
      fontWeight: 600,
      boxShadow: "none",
      background: ap.accent,
      color: ap.accentContrast,
      "&:hover": { background: ap.accent, filter: "brightness(1.08)", boxShadow: "none" },
    },
    rowBetween: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" },
    link: { color: ap.accent, fontWeight: 600 },
    muted: { fontSize: 14, color: ap.textMuted, textAlign: "center" },
  };
});

const MOCK = [
  { ini: "MF", name: "María Fernández", msg: "mockMsg1", time: "10:42", n: 2, bg: "#0F766E" },
  { ini: "JQ", name: "Jorge Quispe", msg: "mockMsg2", time: "10:15", n: 1, bg: "#9333EA" },
  { ini: "AT", name: "Andrea Torres", msg: "mockMsg3", time: "09:58", n: 0, bg: "#C2410C" },
];

const AuthLayout = ({ title, subtitle, children }) => {
  const classes = useStyles();
  const theme = useTheme();
  const { webInfo } = useWebInfo();
  const backendUrl =
    window.WATOOLX_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const [logo] = useState(() => `${backendUrl}/public/logotipos/login.png?r=${Date.now()}`);
  const ap = authPalette(theme.mode);

  // Doc 0446: si la plataforma subió una imagen de fondo, ocupa el panel entero.
  // Se lee del endpoint público porque aquí todavía no hay sesión.
  const [background, setBackground] = useState(null);
  useEffect(() => {
    let cancelled = false;
    getWebInfo()
      .then(({ data }) => !cancelled && setBackground(data?.loginBackgroundUrl || null))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className={classes.root}>
      <CssBaseline />

      <main className={classes.formSide}>
        <div className={classes.brandRow}>
          <img src={logo} alt="" className={classes.logo} />
          {webInfo?.title && <span className={classes.brandName}>{webInfo.title}</span>}
        </div>

        <div className={classes.formWrap}>
          {title && (
            <Typography component="h1" className={classes.title}>
              {title}
            </Typography>
          )}
          {subtitle && <Typography className={classes.subtitle}>{subtitle}</Typography>}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>{children}</div>
        </div>

        <footer className={classes.footer}>
          © {new Date().getFullYear()}{" "}
          <span style={{ color: ap.accent, fontWeight: 600 }}>{webInfo?.companyName || "watoolx.com"}</span>
          <span className={classes.version}>{getVersionText()}</span>
        </footer>
      </main>

      {background ? (
        <aside
          className={classes.brandImage}
          style={{ backgroundImage: `url("${background}")` }}
          aria-hidden="true"
        />
      ) : (
      <aside className={classes.brandSide} aria-hidden="true">
        <div className={classes.glow} />
        <div className={classes.heroTitle}>{i18n.t("authScreens.heroTitle")}</div>
        <div className={classes.heroSubtitle}>{i18n.t("authScreens.heroSubtitle")}</div>

        <div className={classes.preview}>
          <div className={classes.previewHead}>
            <span className={`${classes.chip} ${classes.chipOn}`}>{i18n.t("tickets.simple.chipOpen")} 7</span>
            <span className={classes.chip}>{i18n.t("tickets.simple.chipPending")} 3</span>
            <span className={classes.chip}>{i18n.t("tickets.simple.chipGroups")}</span>
          </div>
          {MOCK.map((m) => (
            <div key={m.ini} className={classes.row}>
              <span className={classes.avatar} style={{ background: m.bg }}>{m.ini}</span>
              <span className={classes.rowText}>
                <div className={classes.rowName}>{m.name}</div>
                <div className={classes.rowMsg}>{i18n.t(`authScreens.${m.msg}`)}</div>
              </span>
              <span className={classes.rowMeta}>
                <span className={classes.rowTime} style={m.n ? undefined : { color: "#6B7280" }}>{m.time}</span>
                {m.n > 0 && <span className={classes.badge}>{m.n}</span>}
              </span>
            </div>
          ))}
        </div>

        <div className={classes.channels}>
          {["WhatsApp", "Instagram", "Messenger", "Telegram"].map((c) => (
            <span key={c} className={classes.channel}>{c}</span>
          ))}
        </div>
      </aside>
      )}
    </div>
  );
};

export default AuthLayout;
